/**
 * Dialog for generating SSO setup links for tenants
 */

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  generateSsoSetupLinkMutation,
  type TenantRead,
} from "@harmonyso/tenants-sdk";
import { cellRequestOptions } from "@/lib/cells/cells";
import { Link, Copy, CheckCircle, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// Helper function to extract error message from unknown error
const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { data?: { detail?: string } } })
      .response;
    if (response?.data?.detail) {
      return response.data.detail;
    }
  }
  if (error && typeof error === "object" && "message" in error) {
    return (error as { message: string }).message;
  }
  return "Unknown error occurred";
};

interface SsoSetupDialogProps {
  tenant: TenantRead;
  trigger?: React.ReactNode;
}

export function SsoSetupDialog({ tenant, trigger }: SsoSetupDialogProps) {
  const [open, setOpen] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const generateSsoLink = useMutation({
    mutationFn: generateSsoSetupLinkMutation().mutationFn,
    onSuccess: (data) => {
      // Reset copy states when generating new link
      setCopiedUrl(false);
      console.log("SSO Link Response:", data);
      toast.success("SSO setup link generated successfully!");
    },
    onError: (error: unknown) => {
      console.error("Failed to generate SSO setup link:", error);
      const errorDetail = getErrorMessage(error);
      toast.error("Failed to generate SSO setup link", {
        description: errorDetail,
      });
    },
  });

  const handleGenerate = () => {
    generateSsoLink.mutate({
      ...cellRequestOptions(tenant.deployment_cell),
      path: { tenant_id: tenant.id },
    });
  };

  const handleCopyUrl = async () => {
    if (generateSsoLink.data?.sso_setup_link) {
      try {
        await navigator.clipboard.writeText(
          generateSsoLink.data.sso_setup_link,
        );
        setCopiedUrl(true);
        setTimeout(() => {
          setCopiedUrl(false);
        }, 2000);
        toast.success("SSO setup link copied to clipboard!");
      } catch (error) {
        console.error("Failed to copy URL:", error);
        toast.error("Failed to copy URL to clipboard");
      }
    }
  };

  const handleOpenUrl = () => {
    if (generateSsoLink.data?.sso_setup_link) {
      window.open(generateSsoLink.data.sso_setup_link, "_blank");
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Reset everything when closing
    generateSsoLink.reset();
    setCopiedUrl(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (newOpen) {
          setOpen(true);
        } else {
          handleClose();
        }
      }}
    >
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm" className="hover:cursor-pointer">
            <Link className="mr-1 h-3 w-3" />
            SSO Setup
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link className="h-5 w-5" />
            Generate SSO Setup Link
          </DialogTitle>
          <DialogDescription>
            Create a secure setup link for configuring SSO authentication for
            this tenant.
          </DialogDescription>
        </DialogHeader>

        {/* Tenant Info */}
        <div className="py-4">
          <div className="bg-muted/50 rounded-lg border p-4">
            <div className="space-y-2">
              <h4 className="font-semibold">{tenant.company}</h4>
              <div className="text-muted-foreground space-y-1 text-sm">
                <div>
                  Subdomain:{" "}
                  <span className="font-mono">{tenant.subdomain}</span>
                </div>
                <div>
                  Tenant ID: <span className="font-mono">{tenant.id}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Generate Button or Results */}
        {!generateSsoLink.data ? (
          <div className="space-y-4">
            <div className="text-muted-foreground text-sm">
              Click the button below to generate a secure SSO setup link for
              this tenant. The link will include temporary credentials for
              initial configuration.
            </div>
            <Button
              onClick={handleGenerate}
              disabled={generateSsoLink.isPending}
              className="w-full"
            >
              {generateSsoLink.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                "Generate SSO Setup Link"
              )}
            </Button>

            {generateSsoLink.error ? (
              <div className="text-destructive bg-destructive/10 rounded-md p-3 text-sm">
                Failed to generate SSO setup link:{" "}
                {generateSsoLink.error instanceof Error
                  ? generateSsoLink.error.message
                  : "Unknown error"}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Success Message */}
            <div className="flex items-center gap-2 rounded-md bg-green-50 p-3 text-sm text-green-600 dark:bg-green-900/20">
              <CheckCircle className="h-4 w-4" />
              SSO setup link generated successfully!
            </div>

            {/* Setup URL */}
            <div className="space-y-2">
              <Label>Setup URL</Label>
              <div className="flex gap-2">
                <Input
                  value={generateSsoLink.data.sso_setup_link}
                  readOnly
                  className="font-mono text-xs"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void handleCopyUrl();
                  }}
                  className="shrink-0"
                >
                  {copiedUrl ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenUrl}
                  className="shrink-0"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Instructions */}
            <div className="text-muted-foreground rounded-md bg-blue-50 p-3 text-sm dark:bg-blue-900/20">
              <div className="mb-1 font-medium">Next Steps:</div>
              <ol className="list-inside list-decimal space-y-1">
                <li>Share the setup URL with the tenant administrator</li>
                <li>The tenant admin can use this link to configure SSO</li>
                <li>
                  The link is valid for a limited time (1 week by default)
                </li>
              </ol>
            </div>

            {/* Generate New Link */}
            <Button
              variant="outline"
              onClick={handleGenerate}
              disabled={generateSsoLink.isPending}
              className="w-full"
            >
              Generate New Link
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
